
import React, { useState } from 'react';
import { useFavorites } from '../../hooks/useFavorites';
import { Listing } from '../../types';
import { Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface FavoriteButtonProps {
    listingId: string;
    listingData?: Partial<Listing>;
    size?: 'sm' | 'md' | 'lg';
    showCount?: boolean;
    className?: string;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
    listingId,
    listingData,
    size = 'md',
    showCount = false,
    className = ''
}) => {
    const { toggleFavorite, isFavorite, favoritesCount, loading } = useFavorites();
    const [isLoading, setIsLoading] = useState(false);
    const [playSpark, setPlaySpark] = useState(false);

    const sizeClasses = {
        sm: 'p-1 text-sm',
        md: 'p-2 text-base',
        lg: 'p-3 text-lg'
    };

    const iconSizes = {
        sm: 16,
        md: 20,
        lg: 24
    };

    const handleClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        if (isLoading || loading) return;

        setIsLoading(true);
        const wasFav = isFavorite(listingId);
        await toggleFavorite(listingId, listingData);
        if (!wasFav) {
            setPlaySpark(true);
            setTimeout(() => setPlaySpark(false), 1000); // Reset for next time
        }
        setIsLoading(false);
    };

    const isFav = isFavorite(listingId);

    return (
        <button
            onClick={handleClick}
            disabled={isLoading || loading}
            className={`
        ${sizeClasses[size]}
        ${className}
        transition-all duration-200
        ${isFav
                    ? 'text-red-500 hover:text-red-600'
                    : 'text-gray-400 hover:text-red-500'
                }
        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        flex items-center justify-center gap-1
        rounded-full hover:bg-red-50
      `}
            aria-label={isFav ? 'Favorilerden çıkar' : 'Favorilere ekle'}
            title={isFav ? 'Favorilerden çıkar' : 'Favorilere ekle'}
        >
            <motion.div
                className="relative"
                animate={playSpark ? {
                    scale: [1, 1.4, 0.9, 1.1, 1],
                    transition: { duration: 0.5, ease: "easeOut" }
                } : {}}
            >
                <Heart
                    size={iconSizes[size]}
                    className={`${isFav ? 'fill-current text-red-500' : 'text-gray-400'} transition-colors duration-300`}
                />
                <AnimatePresence>
                    {playSpark && (
                        <>
                            <motion.div
                                initial={{ scale: 0, opacity: 1 }}
                                animate={{ scale: 2.5, opacity: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                                className="absolute inset-0 border-2 border-red-500 rounded-full pointer-events-none"
                            />
                            {[...Array(6)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                                    animate={{
                                        x: Math.cos(i * 60 * Math.PI / 180) * 20,
                                        y: Math.sin(i * 60 * Math.PI / 180) * 20,
                                        scale: 0.5,
                                        opacity: 0
                                    }}
                                    transition={{ duration: 0.5, ease: "backOut" }}
                                    className="absolute inset-0 m-auto w-1.5 h-1.5 bg-red-400 rounded-full pointer-events-none"
                                />
                            ))}
                        </>
                    )}
                </AnimatePresence>
            </motion.div>

            {showCount && favoritesCount > 0 && (
                <span className="text-xs font-medium ml-1">
                    {favoritesCount}
                </span>
            )}

            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-gray-950/50 rounded-full">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-500 border-t-transparent"></div>
                </div>
            )}
        </button>
    );
};

export default FavoriteButton;
